import { Button, Layout, Menu, Tabs } from 'antd'
import { useContext, useEffect, useState } from 'react'
import { AuthenticationContext } from '../../AuthenticationContext'
import SuccessChecklistReport from '../SuccessChecklist/SuccessChecklistReport'
import TenantLastActivityTable from '../admin/TenantLastActivityReport/TenantLastActivityTable'
import UserCountReport from '../admin/UserCountReport/UserCountReport'
import TenantReport from '../admin/TenantReport/TenantReport'
import ProposalsReport from '../admin/TenantReport/ProposalsReport'
import TenantBudgetReport from '../admin/TenantBudgetReport/TenantBudgetReport'
import InvoicingProgress from '../admin/InvoicingProgress/InvoicingProgress'
import InvoicingFunnel from '../admin/InvoicingFunnel/InvoicingFunnel'
import InvoicingFunnelHistory from '../admin/InvoicingFunnel/InvoicingFunnelHistory'
import PaymentsRetention from '../admin/PaymentsRetention/PaymentsRetention'
import ChatSearchPanel from './Chats/ChatSearchPanel'
import SpendPanel from './Chats/Spend/SpendPanel'
import EvalSection from './Eval/EvalSection'
import TranscriptsSection from './Transcripts/TranscriptsSection'
import FeaturesSection from './Features/FeaturesSection'
import RainforestEventsSection from './RainforestEvents/RainforestEventsSection'
import OrphanedRouteEventsSection from './OrphanedRouteEvents/OrphanedRouteEventsSection'
import PaymentsResetSection from './PaymentsReset/PaymentsResetSection'
import RebuildSnapshotsSection from './RebuildSnapshots/RebuildSnapshotsSection'
import JobsToInvoiceCompareSection from './JobsToInvoiceCompare/JobsToInvoiceCompareSection'
import TokensSection from './Tokens/TokensSection'
import StatementRepairSection from './StatementRepair/StatementRepairSection'
import TaxJurisdictionAuditSection from './TaxJurisdictionAudit/TaxJurisdictionAuditSection'
import QuickBooksResyncSection from './QuickBooksResync/QuickBooksResyncSection'
import InvoiceCheckerSection from './InvoiceChecker/InvoiceCheckerSection'
import MarkUnbillableSection from './MarkUnbillable/MarkUnbillableSection'
import UndatedWebEntriesSection from './UndatedWebEntries/UndatedWebEntriesSection'
import CuratedTemplatesSection from './CuratedTemplates/CuratedTemplatesSection'
import LongRunningTasksSection from './LongRunningTasks/LongRunningTasksSection'
import { Section, buildAdminAppHash, getSectionFromHash } from './adminAppShellModel'
import './AdminAppShell.css'

const { Header, Content } = Layout

const AdminAppShell = () => {
  const { currentUser } = useContext(AuthenticationContext)
  const [activeSection, setActiveSection] = useState<Section>(() => getSectionFromHash(window.location.hash))

  useEffect(() => {
    if (currentUser && !currentUser.isSuperUser) {
      window.location.hash = '#!/'
    }
  }, [currentUser])

  if (!currentUser?.isSuperUser) {
    return null
  }

  return (
    <Layout className="admin-app-shell">
      <Header className="admin-header">
        <span className="admin-header-title">SynkedUP Admin</span>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[activeSection]}
          onClick={({ key }) => {
            setActiveSection(key as Section)
            window.location.hash = buildAdminAppHash(key as Section)
          }}
          items={[
            { key: 'reports', label: 'Reports' },
            { key: 'chats', label: 'Chats' },
            { key: 'transcripts', label: 'Transcripts' },
            { key: 'eval', label: 'Eval' },
            { key: 'tokens', label: 'Tokens' },
            { key: 'features', label: 'Features' },
            { key: 'invoicing', label: 'Invoicing' },
            { key: 'routes', label: 'Routes' },
            { key: 'snapshots', label: 'Snapshots' },
            { key: 'curated-templates', label: 'Curated Templates' },
            { key: 'long-running-tasks', label: 'Long-Running Tasks' },
          ]}
        />
        <Button
          type="link"
          style={{ color: '#fff' }}
          onClick={() => { window.location.hash = '#!/' }}
        >
          Back to App
        </Button>
      </Header>
      <Content className="admin-content">
        {activeSection === 'reports' && <ReportsSection />}
        {activeSection === 'chats' && <ChatsSection />}
        {activeSection === 'transcripts' && <TranscriptsSection />}
        {activeSection === 'eval' && <EvalSection />}
        {activeSection === 'tokens' && <TokensSection />}
        {activeSection === 'features' && <FeaturesSection />}
        {activeSection === 'invoicing' && <InvoicingSection />}
        {activeSection === 'routes' && <OrphanedRouteEventsSection />}
        {activeSection === 'snapshots' && <SnapshotsSection />}
        {activeSection === 'curated-templates' && <CuratedTemplatesSection />}
        {activeSection === 'long-running-tasks' && <LongRunningTasksSection />}
      </Content>
    </Layout>
  )
}

const ReportsSection = () => (
  <Tabs
    defaultActiveKey="success-checklist"
    items={[
      { key: 'success-checklist', label: 'Success Checklist', children: <SuccessChecklistReport /> },
      { key: 'last-activity', label: 'Tenant Last Activity', children: <TenantLastActivityTable /> },
      { key: 'user-count', label: 'User Count', children: <UserCountReport /> },
      { key: 'tenant-report', label: 'Tenant Report', children: <TenantReport /> },
      { key: 'proposals', label: 'Proposals', children: <ProposalsReport /> },
      { key: 'tenant-budget', label: 'Tenant Budget', children: <TenantBudgetReport /> },
      { key: 'invoicing', label: 'Invoicing', children: <InvoicingReportsSection /> },
    ]}
  />
)

const InvoicingReportsSection = () => (
  <Tabs
    defaultActiveKey="progress"
    items={[
      { key: 'progress', label: 'Progress', children: <InvoicingProgress /> },
      { key: 'funnel', label: 'Funnel', children: <InvoicingFunnel /> },
      { key: 'funnel-history', label: 'Funnel History', children: <InvoicingFunnelHistory /> },
      { key: 'payments-retention', label: 'Payments Retention', children: <PaymentsRetention /> },
    ]}
  />
)

const ChatsSection = () => (
  <Tabs
    defaultActiveKey="search"
    items={[
      { key: 'search', label: 'Search', children: <ChatSearchPanel /> },
      { key: 'spend', label: 'Spend', children: <SpendPanel /> },
    ]}
  />
)

const InvoicingSection = () => (
  <Tabs
    defaultActiveKey="reprocess-event"
    items={[
      { key: 'reprocess-event', label: 'Reprocess Event', children: <RainforestEventsSection /> },
      { key: 'payments-reset', label: 'Payments Reset', children: <PaymentsResetSection /> },
      { key: 'tax-jurisdiction-audit', label: 'Tax Jurisdiction Audit', children: <TaxJurisdictionAuditSection /> },
      { key: 'statement-repair', label: 'Statement Repair', children: <StatementRepairSection /> },
      { key: 'quickbooks-resync', label: 'QuickBooks Re-sync', children: <QuickBooksResyncSection /> },
      { key: 'invoice-checker', label: 'Invoice Checker', children: <InvoiceCheckerSection /> },
      { key: 'mark-unbillable', label: 'Mark Unbillable', children: <MarkUnbillableSection /> },
      { key: 'undated-web-entries', label: 'Undated Web Entries', children: <UndatedWebEntriesSection /> },
    ]}
  />
)

const SnapshotsSection = () => (
  <Tabs
    defaultActiveKey="rebuild"
    items={[
      { key: 'rebuild', label: 'Rebuild', children: <RebuildSnapshotsSection /> },
      { key: 'compare', label: 'Compare', children: <JobsToInvoiceCompareSection /> },
    ]}
  />
)

export default AdminAppShell
